import { useMutation } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import GraphqlService from '../service/graphqlService';
import { useSnackbar } from '../providers/SnackbarContext';

export const AUDIT_ENTITY_CERTIFICATE_PROVISIONING = 'CertificateProvisioning';
export const AUDIT_ENTITY_DEVICE_DEFENDER = 'DeviceDefender';
export const AUDIT_ENTITY_PORTAL_COGNITO_USERS = 'PortalCognitoUsers';
export const AUDIT_CHANGE_CREDENTIAL_CREATION = 'CredentialCreation';
export const AUDIT_CHANGE_FIELD_UPDATE = 'FieldUpdate';
export const AUDIT_CHANGE_QUOTA_INCREASE = 'QuotaIncrease';
export const AUDIT_CHANGE_CREDENTIAL_DISABLED = 'CredentialDisabled';
export const AUDIT_CHANGE_CREDENTIAL_DELETED = 'CredentialDeleted';
export const AUDIT_CHANGE_DEVICE_BLOCKED = 'DeviceBlocked';
export const AUDIT_CHANGE_DEVICE_UNBLOCKED = 'DeviceUnblocked';
// User-management change types. The backend writes these audit entries
// server-side on success (see backend `main.ts` / `auditActions.ts`); these
// constants are kept in sync for client-side display/consistency only.
export const AUDIT_CHANGE_USER_CREATED = 'UserCreated';
export const AUDIT_CHANGE_USER_DELETED = 'UserDeleted';
export const AUDIT_CHANGE_ROLE_ADDED = 'RoleAdded';
export const AUDIT_CHANGE_ROLE_REMOVED = 'RoleRemoved';

/**
 * Custom hook for audit logging functionality
 * 
 * @param {Object} options - Hook options
 * @param {boolean} options.silent - If true, errors will only be logged to console (no snackbar)
 * @returns {Object} An object containing the logAuditChange function and audit constants
 */
export function useAuditLog({ silent = false } = {}) {
  const openSnackBar = useSnackbar();

  const createAuditLogMutation = useMutation(
    (auditData) => GraphqlService.createAuditLog(auditData),
    {
      onError: (error) => {
        console.error('[useAuditLog] Failed to record audit log:', error);
        if (!silent) {
          openSnackBar('Failed to record change in history.', 'error');
        }
      }
    }
  );

  /**
   * Log an audit change
   * 
   * @param {string} entity - The entity type being audited
   * @param {string} changeType - The type of change being made
   * @param {string} entityId - The ID of the entity being changed
   * @param {string} detail - Optional detail about the change
   * @param {string} value - Optional value being set
   * @returns {Promise<void>}
   */
  const logAuditChange = async (entity, changeType, entityId, detail = null, value = null) => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const userId = currentUser?.username;
      const userName = currentUser?.attributes?.email;

      createAuditLogMutation.mutate({
        entity,
        entity_id: String(entityId),
        change: changeType,
        detail,
        value: value ? String(value) : null,
        user_id: userId || userName,
        user_name: userName || userId,
      });
    } catch (error) {
      console.error('Error logging audit change:', error);
      createAuditLogMutation.mutate({
        entity,
        entity_id: String(entityId),
        change: changeType,
        detail,
        value: value ? String(value) : null,
        user_id: 'Unknown',
        user_name: 'Unknown',
      });
    }
  };

  return {
    logAuditChange,
    createAuditLogMutation,
  };
}
